import * as d3 from 'd3';

import { EquityIndicator, Indicator } from "./Types";

export const theme = {
    fontSize: 13,
    grey: {primary: '#757575', secondary: '#9d9d9d', tertiary: '#EAEAEA'},
    backgroundFill: '#FAF6F0',
    focusColor: '#1D618E',
    focusColorDark: '#113A55',
    darkGradientColor: "#113A55",
    fontFamily: 'Inter',
    highlightOpacity: .7,
    nonHighlightOpacity: .3,
    lineHeight: 1.2,
    missingDataFill: "#d6d6d6"
}

export const layersStyle = {default: { color: theme.grey.primary, fillColor: theme.backgroundFill, fillOpacity: theme.highlightOpacity, weight: 1 },
                            outline: { color: theme.grey.primary, fill: false, weight: 2 },
                            greyOut: { color: theme.grey.secondary, fillOpacity: 0.7, weight: 0},
                            State: {
                              highlight: { weight: 2,
                                           fillOpacity: .8 },
                            },
                            VD: {
                              highlight: { weight: 3,
                                           fillColor: theme.focusColorDark,
                                           fillOpacity: theme.nonHighlightOpacity}
                            }
                          }
                          
function getStrokeOpacity(d: any) {
  return d ? 1 : theme.nonHighlightOpacity;
}

export function getFillOpacity(d: any) {
  return d ? theme.highlightOpacity : theme.nonHighlightOpacity;
}

function getWeight(d: any) {
  return d ? 3 : 2;
}

export function highlightInnerGeography(feature: any) {

  return {
    weight: getWeight(feature.properties!.selected),
    fillOpacity: feature.properties!.selected? .8: theme.nonHighlightOpacity
  }
}

// Selected county styles
export function highlightGeographicBoundary(feature: any, equityIndicator: EquityIndicator, showEquityIndicator: boolean, showCensusTracts: boolean) {

  let color = theme.grey.primary;
  let fillColor = theme.backgroundFill;
  let fill = false;

  if (showEquityIndicator) {
    color = theme.focusColorDark;
  } else {
    color = theme.grey.primary;
  }

  if (feature.properties.type === 'County') {
    color = showEquityIndicator? theme.darkGradientColor: theme.grey.primary;

    if (feature.properties!.changeYearData !== undefined) {

      if (feature.properties!.changeYearData.pollSummary.missingDataFlag) {
        fillColor = "#d6d6d6"; 
      } else if (showEquityIndicator){
        fillColor =  feature.properties!.changeYearData[equityIndicator.variable].fillColor;
      } else {
        fillColor = theme.backgroundFill;
      }
    }
    fill = showCensusTracts? false: true;
  }

  return {
    color: color,
    weight: getWeight(feature.properties!.selected),
    opacity: getStrokeOpacity(feature.properties!.selected),
    fill: fill,
    fillColor: fillColor,
    fillOpacity: showCensusTracts? 0: getFillOpacity(feature.properties!.selected)
  };
}

export function choroplethStyle(feature: any, equityIndicator: EquityIndicator, showEquityIndicator: boolean) {

  let color = theme.grey.primary;
  let fillColor = theme.backgroundFill;

  if (feature.properties.type === 'Voting district') {
    return vdStyle(feature);
  } else {

    if (feature.properties!.changeYearData !== undefined) {
       
      // Equity Indicator Fill 
      if (showEquityIndicator) {
        color = theme.darkGradientColor;
        fillColor = feature.properties!.changeYearData[equityIndicator.variable].fillColor;
      } else if (feature.properties!.changeYearData.pollSummary.missingDataFlag) {
        fillColor = "#d6d6d6"; 
      }
    }

    return {
      color: color,
      fillColor: fillColor,
      weight: 1,
      opacity: getStrokeOpacity(feature.properties!.selected),
      fillOpacity: getFillOpacity(feature.properties!.selected)
    }
  }
}

// Voting district styles
export function vdStyle(feature: any) {
  return {
    color: theme.focusColorDark,
    weight: 1,
    opacity: getStrokeOpacity(feature.properties!.selected),
    fillOpacity: 0
  }
}

// Poll styles
export function pollStyle(point: any, selected: boolean, indicator: Indicator, showEquityIndicator: boolean) {

  let fillColor;
  let color;
  if (point.type === "Poll") {

    if(showEquityIndicator && point.status === "No change") {
      color = theme.grey.secondary;
      fillColor = '#CCCCCC';
    } else {
      color = pollStrokeScale(point.status) as string;
      fillColor = pollStrokeScale(point.status) as string;
    }
    
  } else {
    if (indicator.variable === "changeNoPolls") {
      fillColor = pollFillScale(overallChangeThresholdScale(point.overallChange).toString());
      color = pollStrokeScale2(overallChangeStrokeThresholdScale(point.overallChange).toString());

      if(showEquityIndicator && overallChangeThresholdScale(point.overallChange).toString() === '0' ) {
        color = theme.grey.secondary;
        fillColor = '#CCCCCC';
      }
    } else {
      fillColor = pollFillScale(changeYearCVAPPerPollThresholdScale(point.changeYearCVAPPerPoll).toString());
      color = pollStrokeScale2(changeYearCVAPPerPollStrokeThresholdScale(point.changeYearCVAPPerPoll).toString());

      if(showEquityIndicator && changeYearCVAPPerPollThresholdScale(point.changeYearCVAPPerPoll).toString() === '0' ) {
        color = theme.grey.secondary;
        fillColor = '#CCCCCC';
      }
    }
  }

  return {
    fillColor: fillColor,
    color: color,
    weight: 1,
    opacity: getStrokeOpacity(selected),
    fillOpacity: getFillOpacity(selected)
  };
}

export function pollSummarySize(point: any, indicator: Indicator) {
  
  let r;
  if (indicator.variable === "changeNoPolls") {
    r = rScale(changeNoPollsThresholdScale(point.changeNoPolls));
  } else {
    r = rScale(baseYearCVAPPerPollThresholdScale(point.baseYearCVAPPerPoll));
  }

  return r*500;
}

// https://coolors.co/f5ece0-320e3b-1d618e-e45729-610063
export const equityIndicatorPctBlackThresholdScale = d3.scaleThreshold([0, 15, 30, 45], [theme.missingDataFill, '#a2c2d8', '#6999b8', '#437da3', '#1d6183']);
export const equityIndicatorPopDensityThresholdScale = d3.scaleThreshold([0, 25, 125, 500, 1000], [theme.missingDataFill, '#C8DEED', '#a2c2d8', '#6999b8', '#437da3', '#1d6183']);
export const equityIndicatorPctHispanicThresholdScale = d3.scaleThreshold([0, 1, 5, 10], [theme.missingDataFill, '#a2c2d8', '#6999b8', '#437da3', '#1d6183']);

export function equityIndicatorScale(equityIndicator: EquityIndicator) {
  if (equityIndicator.variable === "baseYearPopDensity") {
    return equityIndicatorPopDensityThresholdScale;
  } else if (equityIndicator.variable === "baseYearPctCVAPHispanic") {
    return equityIndicatorPctHispanicThresholdScale;
  } else {
    return equityIndicatorPctBlackThresholdScale;
  }
}

export const changeNoPollsThresholdScale = d3.scaleThreshold([1, 5, 15, 30], [1, 2, 5, 15, 30]);
export const baseYearCVAPPerPollThresholdScale = d3.scaleThreshold([500, 1000, 2500, 5000], [2.5, 5, 10, 25, 50]);

export const overallChangeThresholdScale = d3.scaleThreshold([-10, -3, 0, 1, 4, 11], [-3, -2, -1, 0, 1, 2, 3]);
export const overallChangeStrokeThresholdScale = d3.scaleThreshold([0, 1], [-3, 0, 3]);

export const changeYearCVAPPerPollThresholdScale = d3.scaleThreshold([-200, -100, -50, 50, 100, 200], [3, 2, 1, 0, -1, -2, -3]);
export const changeYearCVAPPerPollStrokeThresholdScale = d3.scaleThreshold([-50, 50], [3, 0, -3]);

export const statusThresholdScale = d3.scaleOrdinal()
  .domain(["Added", "No change", "Removed"] )
  .range(["3", "0", "-3"] );

export const pollStrokeScale = d3.scaleOrdinal()
  .domain(["Added", "No change", "Removed"] )
  .range(["#610063", theme.grey.primary, "#E45729"] );

export const pollStrokeScale2 = d3.scaleOrdinal()
  .domain(["3", "0", "-3"])
  .range(["#610063", theme.grey.primary, "#E45729"] );

export const pollFillScale = d3.scaleOrdinal()
  .domain(['-3', '-2', '-1', '0', '1', '2', '3'] )
  .range(["#E45729", "#F28559", "#FBB18A", theme.grey.secondary, "#C498A6", "#935485", "#610063"] );

export const rScale = d3.scaleSqrt()
  .domain([1, 30])
  .range([3, 15]);

export const sizeData = [{id: 0, threshold: 1, label: '0', indicator: 'changeNoPolls'},
                        {id: 1, threshold: 2, label: "Between 1 and 5", indicator: 'changeNoPolls'},
                        {id: 3, threshold: 5, label: "Between 6 and 15", indicator: 'changeNoPolls'},
                        {id: 4, threshold: 15, label: "Between 16 and 30", indicator: 'changeNoPolls'},
                        {id: 5, threshold: 30, label: "Greater than 30", indicator: 'changeNoPolls'},
                        {id: 6, threshold: 2.5, label: 'Less than 500', indicator: 'changeYearCVAPPerPoll' },
                        {id: 7, threshold: 5, label: 'Between 500 and 1000', indicator: 'changeYearCVAPPerPoll' },
                        {id: 8, threshold: 10, label: 'Between 1000 and 2500', indicator: 'changeYearCVAPPerPoll' },
                        {id: 9, threshold: 25, label: 'Between 2500 and 5000', indicator: 'changeYearCVAPPerPoll' },
                        {id: 10, threshold: 50, label: 'Greater than 5000', indicator: 'changeYearCVAPPerPoll' },
                      ];

export const colorData =  [{ id: 0, status: 'Added', label: 'Gain of 10 or more polls', overallChange: '3', geo: 'state', indicator: 'changeNoPolls', shortDescr: '10+' },
                           { id: 1, status: 'Added', label: "Gain of between 4 to 10 polls", overallChange: '2', geo: 'state', indicator: 'changeNoPolls', shortDescr: '4-10' },
                           { id: 2, status: 'Added', label: "Gain of between 1 to 3 polls" , overallChange: '1', geo: 'state', indicator: 'changeNoPolls', shortDescr: '1-3' },
                           { id: 3, status: 'No change', label: "No change", overallChange: '0', geo: 'state', indicator: 'changeNoPolls', shortDescr: '0' },
                           { id: 4, status: 'Removed', label: "Loss of between 1 and 3", overallChange: '-1', geo: 'state', indicator: 'changeNoPolls', shortDescr: '-1–3' },
                           { id: 5, status: 'Removed', label: "Loss of between 4 and 10", overallChange: '-2', geo: 'state', indicator: 'changeNoPolls', shortDescr: '-4–10' },
                           { id: 6, status: 'Removed', label: "Loss of 10 or more polls", overallChange: '-3', geo: 'state', indicator: 'changeNoPolls', shortDescr: '-10' },
                           { id: 7, status: 'Added', label: 'Added', overallChange: '3', geo: 'county', indicator: 'changeNoPolls', shortDescr: '' },
                           { id: 8, status: 'No change', label: "No change", overallChange: '0', geo: 'county', indicator: 'changeNoPolls', shortDescr: '' },
                           { id: 9, status: 'Removed', label: "Removed", overallChange: '-3', geo: 'county', indicator: 'changeNoPolls', shortDescr: '' },
                           { id: 10, status: 'Added', label: 'Decrease of 200 or more', overallChange: '3', geo: 'state', indicator: 'changeYearCVAPPerPoll', shortDescr: '-200' },
                           { id: 11, status: 'Added', label: "Decrease of 100 – 200",overallChange: '2', geo: 'state', indicator: 'changeYearCVAPPerPoll', shortDescr: '-100' },
                           { id: 12, status: 'Added', label: "Decrease of 50 – 100" , overallChange: '1', geo: 'state', indicator: 'changeYearCVAPPerPoll', shortDescr: '-50' },
                           { id: 13, status: 'No change', label: "No change", overallChange: '0', geo: 'state', indicator: 'changeYearCVAPPerPoll', shortDescr: '0' },
                           { id: 14, status: 'Removed', label: "Increase of 50 – 100", overallChange: '-1', geo: 'state', indicator: 'changeYearCVAPPerPoll', shortDescr: '+50' },
                           { id: 15, status: 'Removed', label: "Increase of 100 – 200", overallChange: '-2', geo: 'state', indicator: 'changeYearCVAPPerPoll', shortDescr: '+100' },
                           { id: 16, status: 'Removed', label: "Increase of 200 or more", overallChange: '-3', geo: 'state', indicator: 'changeYearCVAPPerPoll', shortDescr: '+200' },
                         ];

export const equityIndicatorData = [{id: 1, variable: 'baseYearPctCVAPBlack', label: 'Less than 15%', value: 10, missing: false},
                                    {id: 2, variable: 'baseYearPctCVAPBlack', label: 'Between 15% and 30%', value: 25, missing: false},
                                    {id: 3, variable: 'baseYearPctCVAPBlack', label: 'Between 30% and 45%', value: 40, missing: false},
                                    {id: 4, variable: 'baseYearPctCVAPBlack', label: 'Greater than 45%', value: 46, missing: false},
                                    {id: 5, variable: 'baseYearPctCVAPBlack', label: 'Missing data', value: -1, missing: true},
                                    {id: 6, variable: 'baseYearPopDensity', label: 'Between 25 to 125', value: 25, missing: false},
                                    {id: 7, variable: 'baseYearPopDensity', label: 'Between 125 to 500', value: 125, missing: false},
                                    {id: 8, variable: 'baseYearPopDensity', label: 'Between 500 to 1000', value: 500, missing: false},
                                    {id: 9, variable: 'baseYearPopDensity', label: 'Greater 1000', value: 1000, missing: false},
                                    {id: 10, variable: 'baseYearPopDensity', label: 'Missing data', value: -1, missing: true},
                                    {id: 11, variable: 'baseYearPctCVAPHispanic', label: 'Less than 1%', value: .5, missing: false},
                                    {id: 12, variable: 'baseYearPctCVAPHispanic', label: 'Between 1% and 5%', value: 4, missing: false},
                                    {id: 13, variable: 'baseYearPctCVAPHispanic', label: 'Between 5% and 10%', value: 7, missing: false},
                                    {id: 14, variable: 'baseYearPctCVAPHispanic', label: 'Greater than 10%', value: 11, missing: false},
                                    {id: 15, variable: 'baseYearPctCVAPHispanic', label: 'Missing data', value: -1, missing: true},
];
